/**
 * `/settings/billing` — **Billing defaults** (C11, FR-A8 §2, §6, §7; T-1.18).
 *
 * The firm-wide switches every later module reads through `get_settings`:
 * the invoice series, the financial year, the place of supply, rounding, and
 * the two that change how work behaves — the **review gate**, which freezes a
 * case until a reviewer approves, and **hours pricing**, which decides whether
 * logged time reaches an invoice at all.
 *
 * - **Place of supply** is chosen from the state list, with *The firm's own
 *   state* first — stored as `null`, meaning *resolve to the firm's state*
 *   (FR-A8 §6).
 * - **Financial year starts in** is a month by name; the column still holds
 *   1–12 (FR-A8 §7).
 *
 * One Save for the page, on the edit bar (Wave 14E, ADR-0032 §5); a dirty
 * form warns before leaving.
 */

import { Alert, Input, Select, Skeleton, Switch } from '@fiveox/ui'
import { CalendarClock, Hash, MapPin, Scale } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { EditBar, useLeaveGuard } from '../../../components/edit'
import { SETTINGS, useEvaluation } from '../../../lib/access'
import { ApiError } from '../../../lib/api'
import { invoiceNumberExample } from '../../../lib/api/billing'
import { type FirmSettings, firm } from '../../../lib/api/firm'
import {
  MONTHS,
  fromSupplyValue,
  monthName,
  monthNumber,
  placeOfSupplyOptions,
  toSupplyValue,
} from './rules'
import { SettingsPage, SettingsSection } from './SettingsPage'

/** The fields this page owns. `approver_roles` is the Approvals page's. */
const FIELDS = [
  'invoice_series_prefix',
  'financial_year_start_month',
  'default_place_of_supply',
  'hours_price_invoices',
  'review_gate_enabled',
  'rounding',
] as const satisfies ReadonlyArray<keyof FirmSettings>

type BillingFields = Pick<FirmSettings, (typeof FIELDS)[number]>

export function billingChanges(original: BillingFields, draft: BillingFields): Partial<BillingFields> {
  const out: Record<string, unknown> = {}
  for (const field of FIELDS) {
    if (draft[field] !== original[field]) out[field] = draft[field]
  }
  return out as Partial<BillingFields>
}

function SwitchRow({
  title,
  hint,
  checked,
  disabled,
  onChange,
}: {
  title: string
  hint: string
  checked: boolean
  disabled: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-4 first:pt-0 last:pb-0">
      <div>
        <p className="text-foreground text-sm font-medium">{title}</p>
        <p className="text-muted-foreground mt-0.5 text-sm">{hint}</p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        srLabel={title}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5"
      />
    </div>
  )
}

export function BillingDefaults() {
  const { can } = useEvaluation()
  const editable = can(SETTINGS, 'can_update')
  const [original, setOriginal] = useState<BillingFields | null>(null)
  const [draft, setDraft] = useState<BillingFields | null>(null)
  const [firmState, setFirmState] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    firm
      .read()
      .then((body) => {
        setOriginal(body.data.settings)
        setDraft(body.data.settings)
        setFirmState(body.data.profile.state ?? null)
      })
      .catch(() => setError('Your billing defaults could not be loaded.'))
  }, [])

  const changes = useMemo(
    () => (original && draft ? billingChanges(original, draft) : {}),
    [original, draft],
  )
  const dirty = Object.keys(changes).length > 0
  const problem = draft && !draft.invoice_series_prefix.trim() ? 'An invoice series needs a prefix.' : null
  const guard = useLeaveGuard(dirty)

  const set = <K extends keyof BillingFields>(field: K, value: BillingFields[K]) => {
    setDraft((prev) => (prev ? { ...prev, [field]: value } : prev))
  }

  const save = async () => {
    if (!dirty || problem) return
    setSaving(true)
    setError(null)
    try {
      const body = await firm.updateSettings(changes)
      setOriginal(body.data)
      setDraft(body.data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Your changes could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsPage
      title="Billing defaults"
      description="How invoices are numbered and taxed, and how work reaches them."
    >
      {guard}
      {error && <Alert tone="error">{error}</Alert>}

      {!draft ? (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-56 w-full rounded-2xl" />
          <Skeleton className="h-40 w-full rounded-2xl" />
        </div>
      ) : (
        <>
          <SettingsSection
            icon={<Hash size={18} aria-hidden />}
            title="Invoice numbering"
            description="Frozen for a financial year once its first invoice is issued."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Invoice series prefix"
                value={draft.invoice_series_prefix}
                maxLength={16}
                disabled={!editable}
                className="font-mono tracking-wide"
                onChange={(e) => set('invoice_series_prefix', e.target.value.toUpperCase())}
                error={problem}
                hint={`Your next invoice this year would be ${invoiceNumberExample(
                  draft.invoice_series_prefix,
                  draft.financial_year_start_month,
                )}.`}
              />
              <Select
                label="Financial year starts in"
                value={monthName(draft.financial_year_start_month)}
                disabled={!editable}
                onChange={(e) =>
                  set('financial_year_start_month', monthNumber(e.target.value) ?? 4)
                }
                hint="April for almost every practice in India."
              >
                {MONTHS.map((month) => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </Select>
            </div>
          </SettingsSection>

          <SettingsSection
            icon={<MapPin size={18} aria-hidden />}
            title="Place of supply"
            description="Decides whether an invoice carries CGST and SGST or IGST."
          >
            <Select
              label="Default place of supply"
              value={toSupplyValue(draft.default_place_of_supply)}
              disabled={!editable}
              onChange={(e) => set('default_place_of_supply', fromSupplyValue(e.target.value))}
              hint={
                firmState
                  ? `A client in ${firmState} is billed CGST and SGST; one elsewhere, IGST. Change it on a single invoice when a job is supplied somewhere else.`
                  : 'Set your firm’s state on Your firm, so “the firm’s own state” means something.'
              }
            >
              {placeOfSupplyOptions(firmState).map((option) => (
                <option key={option.value || 'own'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </SettingsSection>

          <SettingsSection
            icon={<Scale size={18} aria-hidden />}
            title="Totals"
            description="Applied to the invoice total, never to a line."
          >
            <SwitchRow
              title="Round invoice totals to the nearest rupee"
              hint="The difference is shown as a round-off line on the invoice."
              checked={draft.rounding === 'nearest_rupee'}
              disabled={!editable}
              onChange={(next) => set('rounding', next ? 'nearest_rupee' : 'none')}
            />
          </SettingsSection>

          <SettingsSection
            icon={<CalendarClock size={18} aria-hidden />}
            title="How work is billed"
            description="Two switches that change how a case behaves."
          >
            <div className="divide-border divide-y">
              <SwitchRow
                title="Review before a case completes"
                hint="A case entering a stage that needs review is frozen until the reviewer approves it."
                checked={draft.review_gate_enabled}
                disabled={!editable}
                onChange={(next) => set('review_gate_enabled', next)}
              />
              <SwitchRow
                title="Logged hours price invoices"
                hint="Off, time is recorded for your own information and never reaches an invoice."
                checked={draft.hours_price_invoices}
                disabled={!editable}
                onChange={(next) => set('hours_price_invoices', next)}
              />
            </div>
          </SettingsSection>

          <EditBar
            dirty={dirty}
            saving={saving}
            problem={problem}
            editable={editable}
            onSave={() => void save()}
            onDiscard={() => setDraft(original)}
          />
        </>
      )}
    </SettingsPage>
  )
}
